import React from 'react';
import { FmBanner } from './FmBanner';
import { FmButton } from './FmButton';
import { FmRoutePage } from './FmComposition';
import './FmErrorBoundary.css';

export interface FmErrorBoundaryProps {
  /**
   * Content that may throw during render. A throw is caught HERE and turned into
   * an in-page FmBanner error state, so it can never bubble to a top-level
   * boundary that would blank the whole app tree.
   */
  children: React.ReactNode;
  /** Error-banner headline. */
  title: React.ReactNode;
  /** Error-banner explanatory copy. */
  description?: React.ReactNode;
  /** Retry button label. When omitted no retry action is shown. */
  retryLabel?: React.ReactNode;
  /**
   * Called when the user clicks retry (in addition to clearing the caught
   * error). Use it to re-trigger the failed data load.
   */
  onRetry?: () => void;
  /**
   * Bumping this value resets the caught-error state — pass a load key (e.g. a
   * request id) so a fresh, successful load clears a previously caught error.
   */
  resetKey?: string | number;
  /**
   * `route` is for a boundary that replaces a whole route: the banner sits in
   * the route page's own gutter and top padding instead of under the app
   * header at the viewport edge. `inline` (default) is a section's boundary.
   */
  placement?: 'inline' | 'route';
  'data-testid'?: string;
}

interface State {
  error: Error | null;
  resetKey?: string | number;
}

/**
 * FmErrorBoundary — reusable in-page render-error boundary for the Lehti AI DS.
 *
 * React only surfaces render-time throws through class error boundaries; a
 * try/catch around an async fetch cannot catch them. Any page section whose
 * children could throw on malformed/late data wraps them here so the failure
 * renders an honest, scoped FmBanner ("could not load … retry") instead of
 * propagating to the app-shell ChunkLoadErrorBoundary (which re-throws non-chunk
 * errors and unmounts the whole tree → blank #root).
 *
 * NO FALLBACK: on error it renders an error state, never fabricated content.
 */
export class FmErrorBoundary extends React.Component<FmErrorBoundaryProps, State> {
  constructor(props: FmErrorBoundaryProps) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromProps(
    props: FmErrorBoundaryProps,
    state: State,
  ): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(): void {
    // Intentionally empty — no console.error (zero-tolerance console policy).
    // The error is surfaced visually via the banner below.
  }

  private handleRetry = (): void => {
    this.setState({ error: null });
    this.props.onRetry?.();
  };

  render(): React.ReactNode {
    const { error } = this.state;
    const {
      children,
      title,
      description,
      retryLabel,
      onRetry,
      placement = 'inline',
      'data-testid': dataTestId,
    } = this.props;

    if (error === null) {
      return children;
    }

    const banner = (
      <FmBanner
        tone="error"
        title={title}
        description={description}
        data-testid={dataTestId}
        action={
          retryLabel ? (
            <FmButton
              onClick={this.handleRetry}
              variant={placement === 'route' ? 'primary' : undefined}
              data-testid={dataTestId ? `${dataTestId}-retry` : undefined}
            >
              {retryLabel}
            </FmButton>
          ) : onRetry ? (
            <FmButton onClick={this.handleRetry}>{'↻'}</FmButton>
          ) : undefined
        }
      />
    );
    return placement === 'route' ? (
      <FmRoutePage fit="shell" className="fm-error-boundary-route">
        {banner}
      </FmRoutePage>
    ) : (
      banner
    );
  }
}

export default FmErrorBoundary;
